import type { UseFormReturn } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { Accordion } from '@core/components';
import { getEntityById, getLabelFromId } from '@core/utils/collection';
import { useAppContext } from '../../context/AppContext';
import { MaterialKind } from '../../enum/materialKind';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { useCustomerById } from '../../hooks/useCustomerById';
import {
  type MitreItemFormValues,
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  formatOrderItemSizeSummary,
  getMaterialSummary,
} from '../../utils/orderItem';
import { SIZE_FIELDS_BY_VESTMENT_CATEGORY } from '../../utils/size';
import { OrderItemSummaryRow } from './OrderItemDialog';
import { RhfColorSelect } from './RhfColorSelect';
import { RhfFabricSelect } from './RhfFabricSelect';
import { RhfMaterialSelect } from './RhfMaterialSelect';

function MitreItemForm({
  expandedSection,
  setExpandedSection,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  return (
    <>
      <Accordion
        title="Body"
        expanded={expandedSection === 'body'}
        onChange={(expanded) => setExpandedSection(expanded ? 'body' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfFabricSelect<MitreItemFormValues>
            name="bodyFabric"
            label="Fabric"
            flag="isMitreBody"
          />
          <RhfColorSelect<MitreItemFormValues>
            name="bodyColor"
            label="Color"
            fabricFieldName="bodyFabric"
          />
        </Box>
      </Accordion>

      <Accordion
        title="Panel"
        expanded={expandedSection === 'panel'}
        onChange={(expanded) => setExpandedSection(expanded ? 'panel' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfFabricSelect<MitreItemFormValues>
            name="panelFabric"
            label="Fabric"
            flag="isMitrePanel"
          />
          <RhfColorSelect<MitreItemFormValues>
            name="panelColor"
            label="Color"
            fabricFieldName="panelFabric"
          />
        </Box>
      </Accordion>

      <Accordion
        title="Lining"
        expanded={expandedSection === 'lining'}
        onChange={(expanded) => setExpandedSection(expanded ? 'lining' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfFabricSelect<MitreItemFormValues>
            name="liningFabric"
            label="Fabric"
            flag="isMitreLining"
          />
          <RhfColorSelect<MitreItemFormValues>
            name="liningColor"
            label="Color"
            fabricFieldName="liningFabric"
          />
        </Box>
      </Accordion>

      <Accordion
        title="Tassel"
        expanded={expandedSection === 'tassel'}
        onChange={(expanded) => setExpandedSection(expanded ? 'tassel' : null)}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <RhfMaterialSelect<MitreItemFormValues>
            name="tassel"
            label="Tassel"
            kind={MaterialKind.Tassel}
          />
        </Box>
      </Accordion>
    </>
  );
}

function MitreItemSummary({
  methods: methodsProp,
}: OrderItemDialogSlotProps<OrderItemFormValues>) {
  const methods = methodsProp as UseFormReturn<MitreItemFormValues>;
  const { sizes, fabrics, colors, tassels } = useAppContext();

  const quantity = methods.watch('quantity');
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const head = methods.watch('head');
  const bodyFabricId = methods.watch('bodyFabric');
  const bodyColorId = methods.watch('bodyColor');
  const panelFabricId = methods.watch('panelFabric');
  const panelColorId = methods.watch('panelColor');
  const liningFabricId = methods.watch('liningFabric');
  const liningColorId = methods.watch('liningColor');
  const tasselId = methods.watch('tassel');

  const { data: forCustomer } = useCustomerById(forCustomerId);

  return (
    <>
      <OrderItemSummaryRow label="Quantity" fadeKey={quantity}>
        <Typography variant="caption" color="text.secondary">
          {quantity ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Size"
        fadeKey={`${sizeId}-${forCustomerId}-${head}`}
      >
        <Typography variant="caption" color="text.secondary">
          {formatOrderItemSizeSummary(
            SIZE_FIELDS_BY_VESTMENT_CATEGORY[VestmentCategory.Mitre],
            {
              sizeName: getLabelFromId(sizes, sizeId),
              size: getEntityById(sizes, sizeId),
              customer: forCustomer,
              head,
            }
          )}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Body"
        fadeKey={`${bodyFabricId}-${bodyColorId}`}
        colorHex={getEntityById(colors, bodyColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {getLabelFromId(fabrics, bodyFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, bodyColorId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Panel"
        fadeKey={`${panelFabricId}-${panelColorId}`}
        colorHex={getEntityById(colors, panelColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {getLabelFromId(fabrics, panelFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, panelColorId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow
        label="Lining"
        fadeKey={`${liningFabricId}-${liningColorId}`}
        colorHex={getEntityById(colors, liningColorId)?.hexCode}
      >
        <Typography variant="caption" color="text.secondary">
          {getLabelFromId(fabrics, liningFabricId) ?? '-'} /{' '}
          {getLabelFromId(colors, liningColorId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
      <OrderItemSummaryRow label="Tassel" fadeKey={tasselId}>
        <Typography variant="caption" color="text.secondary">
          {getMaterialSummary(tassels, tasselId) ?? '-'}
        </Typography>
      </OrderItemSummaryRow>
    </>
  );
}

const MitreItemDialogContent = {
  renderForm: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <MitreItemForm {...slot} />
  ),
  renderSummary: (slot: OrderItemDialogSlotProps<OrderItemFormValues>) => (
    <MitreItemSummary {...slot} />
  ),
};

export default MitreItemDialogContent;
